import dayjs from 'dayjs'
import { getMobileValidationLength } from '../generalUtils'

// Validation rule function type
export type ValidationRule<T = any> = (value: T, allValues?: Record<string, any>) => string | null

// Validation field configuration
export interface ValidationFieldConfig {
    isError: boolean
    errorMessage: string
    rules: ValidationRule[]
    touched: boolean
}

// Validation state structure
export interface ValidationState {
    [key: string]: ValidationFieldConfig
}

// Common validation rules
export const commonValidationRulesFn = (t: any, referenceDate?: dayjs.Dayjs) => {
    // Use reference date if provided, otherwise fall back to current date
    const getReferenceDate = () => referenceDate || dayjs(undefined, undefined, 'en')

    return {
        // Required field validation
        required: (value: any): string | null => {
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                return t('thisFieldIsRequired')
            }
            return null
        },

        // Email validation
        email: (value: string): string | null => {
            if (!value) return null // Let required rule handle empty check
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(value)) {
                return t('pleaseEnterValidEmailAddress')
            }
            return null
        },

        // Phone number validation
        phone: (value: { mobile: string; mobileCountryCode: string }): string | null => {
            // if (!value) return null
            // if (!value.mobile || (typeof value.mobile === 'string' && value.mobile.trim() === '')) {
            //     return t('thisFieldIsRequired')
            // }
            //only numbers allowed
            const phoneRegex = /^\d*$/
            if (!phoneRegex.test(value.mobile)) {
                return t('pleaseEnterValidPhoneNumber')
            }
            const mobileValidationLength = getMobileValidationLength(value.mobileCountryCode)
            if (value.mobile.length < mobileValidationLength.min || value.mobile.length > mobileValidationLength.max) {
                return t('pleaseEnterValidPhoneNumber')
            }
            return null
        },

        // Minimum length validation
        minLength:
            (min: number) =>
                (value: string): string | null => {
                    if (!value) return null
                    if (value.length < min) {
                        return t('minimumLength', { min })
                    }
                    return null
                },

        // Maximum length validation
        maxLength:
            (max: number) =>
                (value: string): string | null => {
                    if (!value) return null
                    if (value.length > max) {
                        return t('maximumLength', { max })
                    }
                    return null
                },

        // Date validation
        validDate: (value: { day: string; month: string; year: string }): string | null => {
            if (!value.day || !value.month || !value.year) return t('thisFieldIsRequired')

            const day = parseInt(value.day, 10)
            const year = parseInt(value.year, 10)

            // Check if day and year parsing was successful
            if (isNaN(day) || isNaN(year)) {
                return t('pleaseEnterValidDate')
            }

            // Validate ranges
            if (day < 1 || day > 31) {
                return t('pleaseEnterValidDate')
            }

            // if (year < 1900 || year > 2100) {
            //     return 'Year must be between 1900 and 2100'
            // }

            // Create date using dayjs with month name
            const date = dayjs(`${value.month} ${day}, ${year}`, 'MMMM D, YYYY', 'en')

            if (!date.isValid()) {
                return t('pleaseEnterValidDate')
            }

            return null
        },

        // Future date validation
        nonPastDate: (value: { day: string; month: string; year: string }): string | null => {
            if (!value.day || !value.month || !value.year) return null

            const day = parseInt(value.day, 10)
            const year = parseInt(value.year, 10)

            // Check if day and year parsing was successful
            if (isNaN(day) || isNaN(year)) {
                return t('pleaseEnterValidDate')
            }

            // Validate ranges
            if (day < 1 || day > 31) {
                return t('pleaseEnterValidDate')
            }

            // if (year < 1900 || year > 2100) {
            //     return 'Year must be between 1900 and 2100'
            // }

            // Create date using dayjs with month name
            const date = dayjs(`${value.month} ${day}, ${year}`, 'MMMM D, YYYY', 'en')

            if (!date.isValid()) {
                return t('pleaseEnterValidDate')
            }

            const refDate = getReferenceDate().startOf('day')
            if (date.isBefore(refDate)) {
                return t('dateCannotBeInThePast')
            }
            return null
        },

        // Past date validation
        nonFutureDate: (value: { day: string; month: string; year: string }): string | null => {
            if (!value.day || !value.month || !value.year) return null

            const day = parseInt(value.day, 10)
            const year = parseInt(value.year, 10)

            // Check if day and year parsing was successful
            if (isNaN(day) || isNaN(year)) {
                return t('pleaseEnterValidDate')
            }

            // Validate ranges
            if (day < 1 || day > 31) {
                return t('pleaseEnterValidDate')
            }

            // if (year < 1900 || year > 2100) {
            //     return 'Year must be between 1900 and 2100'
            // }

            // Create date using dayjs with month name
            const date = dayjs(`${value.month} ${day}, ${year}`, 'MMMM D, YYYY', 'en')

            if (!date.isValid()) {
                return t('pleaseEnterValidDate')
            }

            const today = dayjs().startOf('day')
            if (date.isAfter(today)) {
                return t('dateCannotBeInTheFuture')
            }
            return null
        },

        // Age validation for adults (must be 12+ years)
        adultAge: (value: { day: string; month: string; year: string }): string | null => {
            if (!value.day || !value.month || !value.year) return null

            const day = parseInt(value.day, 10)
            const year = parseInt(value.year, 10)

            // Check if day and year parsing was successful
            if (isNaN(day) || isNaN(year)) {
                return t('pleaseEnterValidDate')
            }

            // Create date using dayjs with month name
            const birthDate = dayjs(`${value.month} ${day}, ${year}`, 'MMMM D, YYYY', 'en')

            if (!birthDate.isValid()) {
                return t('pleaseEnterValidDate')
            }

            const refDate = getReferenceDate()
            const age = refDate.diff(birthDate, 'year')

            if (age < 12) {
                return t('adultTravelersMustBe12YearsOrOlder')
            }

            return null
        },

        // Age validation for children (must be 2-12 years)
        childAge: (value: { day: string; month: string; year: string }): string | null => {
            if (!value.day || !value.month || !value.year) return null

            const day = parseInt(value.day, 10)
            const year = parseInt(value.year, 10)

            // Check if day and year parsing was successful
            if (isNaN(day) || isNaN(year)) {
                return t('pleaseEnterValidDate')
            }

            // Create date using dayjs with month name
            const birthDate = dayjs(`${value.month} ${day}, ${year}`, 'MMMM D, YYYY', 'en')

            if (!birthDate.isValid()) {
                return t('pleaseEnterValidDate')
            }

            const refDate = getReferenceDate()
            const age = refDate.diff(birthDate, 'year')

            if (age < 2 || age >= 12) {
                return t('childTravelersMustBeBetween2And11YearsOld')
            }

            return null
        },

        // Age validation for infants (must be under 2 years)
        infantAge: (value: { day: string; month: string; year: string }): string | null => {
            if (!value.day || !value.month || !value.year) return null

            const day = parseInt(value.day, 10)
            const year = parseInt(value.year, 10)

            // Check if day and year parsing was successful
            if (isNaN(day) || isNaN(year)) {
                return t('pleaseEnterValidDate')
            }

            // Create date using dayjs with month name
            const birthDate = dayjs(`${value.month} ${day}, ${year}`, 'MMMM D, YYYY', 'en')

            if (!birthDate.isValid()) {
                return t('pleaseEnterValidDate')
            }

            const refDate = getReferenceDate()
            const age = refDate.diff(birthDate, 'year')

            if (age >= 2) {
                return t('infantTravelersMustBeUnder2YearsOld')
            }

            return null
        },
        onlyNumbersAndLetters:
            (errorMessage: string, spaceAllowed: boolean = false) =>
                (value: string): string | null => {
                    if (!value) return null
                    const regex = spaceAllowed ? /^[a-zA-Z0-9\s]+$/ : /^[a-zA-Z0-9]+$/
                    if (!regex.test(value)) {
                        return t(errorMessage)
                    }
                    return null
                },
        onlyLetters:
            (errorMessage: string) =>
                (value: string): string | null => {
                    if (!value) return null
                    const regex = /^[a-zA-Z\s]+$/
                    if (!regex.test(value)) {
                        return t(errorMessage)
                    }
                    return null
                }
    }
}

// Validation hook return type
export interface ValidationHookReturn {
    validationState: ValidationState
    validateField: (fieldName: string, value: any, allValues?: Record<string, any>) => boolean
    validateAll: (values: Record<string, any>) => boolean
    clearFieldError: (fieldName: string) => void
    setFieldError: (fieldName: string, errorMessage?: string, touched?: boolean) => void
    clearAllErrors: () => void
    setFieldTouched: (fieldName: string, touched?: boolean) => void
    hasErrors: boolean
    isValid: boolean
}
